import type { APIResult, APIResultNoContent } from "./types";

import {
  deleteRoutePlans,
  getRoutePlans,
  patchRoutePlans,
  postRoutePlans,
} from "./generated/calls/route-plans/route-plans";
import type {
  RoutePlans as TripDTO,
  RoutePlansInsert as TripInsertDTO,
  RoutePlansUpdate as TripUpdateDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";

export type { TripDTO, TripInsertDTO, TripUpdateDTO };

/**
 * The shape a caller actually supplies on create.
 *
 * `id` has a database default (`uuidv7()`) that PostgREST's generated spec
 * can't see (it only reports simple literal defaults, not function-call
 * ones - see `tools/postgrest-openapi/src/schema.ts`'s `buildInsertSchema`
 * doc comment), which is why `TripInsertDTO` types it as required anyway.
 * Omitting it here lets that default fire.
 *
 * `created_by`/`assigned_driver_id` also have a `current_user_id()` default
 * on paper, but it comes back `null` in practice when omitted from a
 * `route_plans` insert (confirmed live: `23502 null value in column
 * "created_by"`) - so unlike `id`, these are sent explicitly by the caller
 * rather than left to the database.
 */
export type TripCreateDTO = Omit<TripInsertDTO, "id">;

/**
 * Fetches all fleet trips from the API.
 *
 * Returns an API result containing the fetched trips, or a controlled
 * failure if the request could not be completed.
 */
export async function fetchFleetTrips(): Promise<APIResult<TripDTO[]>> {
  try {
    const headers = buildHeaders();
    const tripFetchRes = await getRoutePlans(undefined, { headers });

    switch (tripFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: tripFetchRes.data,
        };
      default:
        throw postgrestError("Fetch failed", tripFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Creates a fleet trip (route plan) in the API.
 *
 * Returns an API result containing the created trip, or a controlled
 * failure if the request could not be completed.
 */
export async function createTrip(
  tripData: TripCreateDTO,
): Promise<APIResult<TripDTO>> {
  try {
    const headers = buildHeaders();
    // `id`/`created_by`/`assigned_driver_id` are intentionally absent from
    // `tripData` (see `TripCreateDTO`) so their database defaults apply -
    // the cast only papers over the generated type over-requiring them.
    const createTripRes = await postRoutePlans(
      tripData as TripInsertDTO,
      undefined,
      { headers: headersToObject(headers) },
    );

    switch (createTripRes.status) {
      case 201: {
        // An insert returns the rows it created, so a single insert returns a
        // one-element array.
        const created = createTripRes.data[0];

        if (!created) {
          throw new Error("Create succeeded but returned no trip");
        }

        return {
          success: true,
          data: created,
        };
      }

      default:
        throw postgrestError("Create failed", createTripRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Updates a fleet trip (route plan) in the API.
 *
 * Returns an API result indicating success, or a controlled failure if the
 * request could not be completed.
 */
export async function updateTrip(
  tripId: string,
  tripData: TripUpdateDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const updateTripRes = await patchRoutePlans(
      tripData,
      { id: postgrestFilter(tripId) },
      { headers: headersToObject(headers) },
    );

    switch (updateTripRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Update failed", updateTripRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Deletes a fleet trip (route plan) from the API.
 *
 * Returns an API result indicating success, or a controlled failure if the
 * request could not be completed.
 */
export async function deleteTrip(
  tripId: string,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const deleteTripRes = await deleteRoutePlans(
      { id: postgrestFilter(tripId) },
      { headers },
    );

    switch (deleteTripRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Delete failed", deleteTripRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}
